import React, {useState, RefObject} from "react"
import styled from "styled-components"

import {H3, Body} from "@irati/ui/src/Typography"
import authentication from "@irati/auth"
import {TextButton as TextButtonBase} from "@irati/ui/src/Button"
import {pollUsersToStringTitle, pollUsersToStringSubtitle} from "@irati/recognition/utils/pollUsersToString"
import FormattedMessage, {InjectIntl, useIntl} from "@irati/utils/lang"
import {POLL} from "@irati/recognition/constants/feedTypes"
import xOfYVotes from "@irati/recognition/utils/polls/xOfYVotes"
import CompanyValues from "@irati-web/companyvalues/src/components/CompanyValuesList"
import {Poll, PollStatus} from "@irati/types"

import {CompanyValuesWrapper, Top, Bottom, EndDate as EndDateBase, Bold} from "../shared/layouts"
import WhoCreated from "../../WhoCreated"
import WhoRewardedBase from "../../WhoRewarded"
import TeamList from "../../TeamList"
import UsersTooltip from "../../shared/UsersTooltip"
import {Activity as IconsActivity} from "../../Icons"
import {AddReaction} from "../../Reactions"
import OccasionIcon from "../../shared/OccasionIcon"
import {Comments, CreateComment} from "../shared/Comments"
import PollActions from "./Actions"

import {VIEW_VOTES} from "./Popup"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import {getPollPublishPath} from "../../../utils/paths"

const WhoRewarded = styled(WhoRewardedBase)`
  margin-bottom: 10px;
`

const EndDate = styled(EndDateBase)`
  margin-top: 10px;
`

const TextButton = styled(TextButtonBase)`
  color: ${({theme}) => theme.colors.perkbox.blue};
`

interface Props extends InjectIntl {
  selectScreen: () => void
  poll: Poll
  onDelete: () => void
}

const ViewPoll = (props: Props) => {
  const {poll, selectScreen, onDelete} = props
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const {formatMessage} = useIntl()
  let textarea: RefObject<T> | null = null

  const pollWinnersResults = poll.winners.results.map(({user}) => user)
  const nbWinners = pollWinnersResults.length
  const title = pollUsersToStringTitle(pollWinnersResults, formatMessage)
  const winner = nbWinners > 0 ? pollWinnersResults[0] : null
  const subTitle = pollUsersToStringSubtitle(pollWinnersResults, poll.title, formatMessage)
  const showReward = nbWinners === 1
  const isAuthor = authentication.user().uuid === poll.createdBy?.uuid
  return (
    <>
      <Top className="center">
        <H3>
          <UsersTooltip placement="bottom" users={poll.winners.results.map(({user}) => user)}>
            <Bold>{title}</Bold>
          </UsersTooltip>
          <br />
          {subTitle}
          <TeamList teams={poll.teams.results} showMobileTooltip={false} />
        </H3>
        <TextButton inline={false} onClick={() => selectScreen(VIEW_VOTES)}>
          {winner && xOfYVotes(poll, winner, formatMessage)}
        </TextButton>
        <CompanyValuesWrapper>
          <OccasionIcon occasion={poll.occasion} />
          <CompanyValues companyValues={poll.companyValues} />
        </CompanyValuesWrapper>
        <EndDate endsAt={poll.endsAt} poll />
        {showReward && <WhoRewarded reward={poll.reward} rewardOrder={poll.rewardOrder} user={poll.createdBy} />}
        <PollActions poll={poll} onDeleteSuccess={onDelete} />
        <Body small marginBottom={1} wordBreak="break-word">
          {poll.desc}
        </Body>
        <WhoCreated createdBy={poll.createdBy} />
        <IconsActivity
          item={poll}
          type={POLL}
          onClickComment={() => {
            if (textarea) {
              textarea.focus()
            }
          }}
          togglePickerModal={() => setShowEmojiPicker(!showEmojiPicker)}
        />
        {showEmojiPicker && <AddReaction item={poll} togglePickerModal={() => setShowEmojiPicker(!showEmojiPicker)} />}
        {isAuthor && poll.status === PollStatus.ClosedUnpublished && (
          <ButtonRouterLink to={getPollPublishPath(poll)} primary marginTop={2}>
            <FormattedMessage id="platform.recognition.polls.btn.closedPollsPublish" />
          </ButtonRouterLink>
        )}
      </Top>
      <Bottom>
        <CreateComment
          item={poll}
          type={POLL}
          key={poll.comments.results.length}
          inputRef={(t: RefObject<T>) => {
            textarea = t
          }}
        />
        <Comments item={poll} />
      </Bottom>
    </>
  )
}
ViewPoll.defaultProps = {
  id: "view-poll"
}

export default ViewPoll
